<template>
    <div class="calendar-page">
        <!-- Filters and Controls -->
        <div class="calendar-toolbar mb-3">
            <div class="row align-items-center">
                <div class="col-auto">
                    <div class="btn-group">
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'month' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'month'"
                        >
                            <i class="fas fa-calendar-alt me-1"></i> Monthly
                        </button>
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'week' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'week'"
                        >
                            <i class="fas fa-calendar-week me-1"></i> Weekly
                        </button>
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'day' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'day'"
                        >
                            <i class="fas fa-calendar-day me-1"></i> Daily
                        </button>
                    </div>
                </div>
                
                <div class="col-auto">
                    <div class="btn-group">
                        <button class="btn btn-sm btn-outline-secondary" @click="previousPeriod">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="goToToday">
                            Today
                        </button>
                        <div class="dropdown" style="position:relative">
                            <button
                                class="btn btn-sm btn-outline-secondary rounded-0 border-start-0"
                                title="Jump to date"
                                @click.stop="showDatePicker = !showDatePicker"
                            >
                                <i class="fas fa-calendar"></i>
                            </button>
                            <div v-if="showDatePicker" class="date-picker-dropdown" @click.stop>
                                <input
                                    type="date"
                                    class="form-control form-control-sm"
                                    :value="pickerValue"
                                    @change="(e) => { goToDate((e.target as HTMLInputElement).value); showDatePicker = false; }"
                                />
                            </div>
                        </div>
                        <button class="btn btn-sm btn-outline-secondary" @click="nextPeriod">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
                
                <div class="col">
                    <h4 class="mb-0">{{ currentPeriodLabel }}</h4>
                </div>
                
                <div class="col-auto">
                    <kt-autoselect
                        label="Task Type"
                        :options="timeTypeOptions"
                        display-by="name"
                        value-by="id"
                        v-model="filters.timeType"
                        @change="applyFilter"
                        :badge-count="(filters.timeType && filters.timeType !== 'All') ? 1 : 0"
                    ></kt-autoselect>
                </div>
                
                <div class="col-auto">
                    <tech-multi-check 
                        label="Technicians"
                        :model-value="filters.technician"
                        @update:model-value="(value: any) => { filters.technician = value; applyFilter(); }"
                    ></tech-multi-check>
                </div>
                
                <div class="col-auto">
                    <div class="btn-group">
                        <button 
                            class="btn btn-sm btn-outline-primary"
                            @click="openTaskModal(null)"
                        >
                            <i class="fas fa-plus me-1"></i>
                            New Task
                        </button>
                        <button
                            v-if="hasActiveFilters"
                            class="btn btn-sm btn-outline-secondary"
                            @click="clearFilters"
                            title="Clear all filters"
                        >
                            <i class="fas fa-times me-1"></i>
                            Clear Filters
                        </button>
                        <button 
                            v-if="selectedTask"
                            class="btn btn-sm btn-primary"
                            @click="openTaskModal(selectedTask)"
                        >
                            <i class="fas fa-edit me-1"></i>
                            Edit Task
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Calendar Views -->
        <div class="calendar-container" @click="deselectTask">
            <!-- Month View -->
            <div v-if="viewMode === 'month'" class="calendar-month">
                <div class="calendar-header">
                    <div class="calendar-day-header" v-for="day in weekDays" :key="day">
                        {{ day }}
                    </div>
                </div>
                <div class="calendar-grid">
                    <div 
                        v-for="(day, index) in monthDays" 
                        :key="index"
                        class="calendar-day"
                        :class="{ 
                            'other-month': !day.isCurrentMonth,
                            'today': day.isToday,
                            'weekend': day.isWeekend,
                            'drag-over': dragOverKey === `month-${index}`
                        }"
                        @dragover.prevent="dragOverKey = `month-${index}`"
                        @dragleave="dragOverKey = null"
                        @drop="handleDrop($event, day.date); dragOverKey = null"
                        @contextmenu.prevent="handleDayContextMenu($event, day.date)"
                    >
                        <div class="day-number">{{ day.dayNumber }}</div>
                        <div class="day-tasks">
                            <div 
                                v-for="task in getTasksForDay(day.date)" 
                                :key="String(task.Id || Math.random())"
                                class="task-item"
                                :class="getTaskClass(task)"
                                draggable="true"
                                @dragstart="handleDragStart($event, task)"
                                @click.stop="selectTask(task)"
                                @contextmenu.prevent="handleTaskContextMenu($event, task)"
                                :title="getTaskTooltip(task)"
                            >
                                <span class="task-compact-line">
                                    <span class="task-compact-time" v-if="task.CompleteByDate || task.WorkCompleteDate">
                                        {{ formatTime(task.CompleteByDate || task.WorkCompleteDate) }}
                                    </span>
                                    <span v-if="(task.Request as any)?.Id" class="task-compact-job">{{ formatJobTitle(task.Request as any) }}</span>
                                    <span class="task-compact-title">{{ getTaskTitle(task) }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Week View -->
            <div v-if="viewMode === 'week'" class="calendar-week">
                <div class="week-grid">
                    <!-- Sticky Header -->
                    <div class="week-header">
                        <div class="time-column-header">Time</div>
                        <div 
                            v-for="day in weekDays" 
                            :key="day" 
                            class="week-day-header"
                            :class="{ 'today': isToday(weekDates[weekDays.indexOf(day)]) }"
                        >
                            <div class="day-name">{{ day }}</div>
                            <div class="day-date">{{ formatDate(weekDates[weekDays.indexOf(day)]) }}</div>
                        </div>
                    </div>
                    
                    <!-- Time Column -->
                    <div class="time-column">
                        <div v-for="hour in 24" :key="hour" class="time-slot">
                            {{ formatHour(hour - 1) }}
                        </div>
                    </div>
                    <div 
                        v-for="(date, dayIndex) in weekDates" 
                        :key="dayIndex"
                        class="week-day-column"
                        :class="{ 'today': isToday(date) }"
                    >
                        <!-- Hour slots with 30-min grid lines -->
                        <div 
                            v-for="hour in 24" 
                            :key="hour"
                            class="week-time-slot"
                            :class="{ 'drag-over': dragOverKey === `week-${dayIndex}-${hour}` }"
                            @dragover.prevent="dragOverKey = `week-${dayIndex}-${hour}`; handleTimeSlotDragOver($event, date, hour - 1)"
                            @dragleave="dragOverKey = null"
                            @drop="handleDropTime($event, date, hour - 1); dragOverKey = null"
                            @contextmenu.prevent="handleDayContextMenu($event, date, hour - 1)"
                        >
                            <!-- 30-minute grid indicator -->
                            <div class="week-time-half"></div>
                        </div>
                        
                        <!-- Positioned events for this day -->
                        <div
                            v-for="positioned in getPositionedTasksForDay(date)"
                            :key="String(positioned.task.Id || Math.random())"
                            class="task-item-week-positioned"
                            :class="[getTaskClass(positioned.task), getOverlapClass(positioned)]"
                            :style="getTaskPositionStyle(positioned)"
                            draggable="true"
                            @dragstart="handleDragStart($event, positioned.task)"
                            @click.stop="selectTask(positioned.task)"
                            @contextmenu.prevent="handleTaskContextMenu($event, positioned.task)"
                        >
                            <div v-if="positioned.task.ActualWork || !positioned.task.WorkAssignment" class="task-type-label-week">{{ getTaskTypeLabel(positioned.task) }}</div>
                            <div class="task-time-week">{{ formatTaskTimeRange(positioned.task) }}</div>
                            <div v-if="(positioned.task.Request as any)?.Id" class="task-job-label-week">{{ formatJobTitle(positioned.task.Request as any) }}</div>
                            <div class="task-title-week">{{ getTaskTitle(positioned.task) }}</div>
                            <div class="task-tech-week" v-if="positioned.task.Technician">
                                <i class="fas fa-user me-1"></i>{{ positioned.task.Technician.TechnicianName }}
                            </div>
                            <div class="task-client-week" v-if="(positioned.task.Request as any)?.Contact?.Company">
                                <i class="fas fa-building me-1"></i>{{ (positioned.task.Request as any).Contact.Company }}
                            </div>
                            <div class="resize-handle" @mousedown.prevent.stop="startResize($event, positioned.task, 'week')"></div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Day View -->
            <div v-if="viewMode === 'day'" class="calendar-day-view">
                <div class="day-view-header">
                    <h5>{{ formatDayHeader(currentDate) }}</h5>
                </div>
                <div class="day-view-grid">
                    <div class="time-column">
                        <div v-for="hour in 24" :key="hour" class="time-slot">
                            {{ formatHour(hour - 1) }}
                        </div>
                    </div>
                    <div class="day-view-column">
                        <!-- Hour slots with 30-min grid lines -->
                        <div 
                            v-for="hour in 24" 
                            :key="hour"
                            class="day-time-slot"
                            :class="{ 'drag-over': dragOverKey === `day-${hour}` }"
                            @dragover.prevent="dragOverKey = `day-${hour}`; handleTimeSlotDragOver($event, currentDate, hour - 1)"
                            @dragleave="dragOverKey = null"
                            @drop="handleDropTime($event, currentDate, hour - 1); dragOverKey = null"
                            @contextmenu.prevent="handleDayContextMenu($event, currentDate, hour - 1)"
                        >
                            <!-- 30-minute grid indicator -->
                            <div class="day-time-half"></div>
                        </div>
                        
                        <!-- Positioned events for the day -->
                        <div
                            v-for="positioned in getPositionedTasksForDay(currentDate)"
                            :key="String(positioned.task.Id || Math.random())"
                            class="task-item-day-positioned"
                            :class="[getTaskClass(positioned.task), getOverlapClass(positioned)]"
                            :style="getTaskPositionStyle(positioned)"
                            draggable="true"
                            @dragstart="handleDragStart($event, positioned.task)"
                            @click.stop="selectTask(positioned.task)"
                            @contextmenu.prevent="handleTaskContextMenu($event, positioned.task)"
                        >
                            <div class="task-time-day">{{ formatTaskTimeRange(positioned.task) }}</div>
                            <div v-if="positioned.task.ActualWork || !positioned.task.WorkAssignment" class="task-type-label-day">{{ getTaskTypeLabel(positioned.task) }}</div>
                            <div v-if="(positioned.task.Request as any)?.Id" class="task-job-label-day">{{ formatJobTitle(positioned.task.Request as any) }}</div>
                            <div class="task-title-day">{{ getTaskTitle(positioned.task) }}</div>
                            <div class="task-tech" v-if="positioned.task.Technician">{{ positioned.task.Technician.TechnicianName }}</div>
                            <div class="task-client" v-if="(positioned.task.Request as any)?.Contact?.Company">{{ (positioned.task.Request as any).Contact.Company }}</div>
                            <div class="resize-handle" @mousedown.prevent.stop="startResize($event, positioned.task, 'day')"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Task Context Menu -->
        <kt-context-menu
            ref="contextMenuRef"
            :items="contextMenuItems"
            min-width="250px"
            @select="handleContextMenuSelect"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Api from '@/services/ApiService';
import Cache from '@/services/Cache';
import { TimeModel } from '@/models/TaskModel';
import { formatJobTitle } from '@/models/RequestModel';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import TechMultiCheck from '@/components/Techs/TechMultiCheck.vue';
import { usePermissions } from '@/composables/usePermissions';
import KtContextMenu from '@/components/UI/KtContextMenu.vue';
import Emitter from '@/services/Emitter';
import type { ContextMenuItem } from '@/components/UI/KtContextMenu.vue';
import dayjs from 'dayjs';
import Helper from '@/services/Helper';

const CACHE_KEY = 'calendar-filter-url';

// Data
const route = useRoute();
const router = useRouter();
const { userId } = usePermissions();
const viewMode = ref<'month' | 'week' | 'day'>('month');
const currentDate = ref(new Date());
const tasks = ref<TimeModel[]>([]);
const selectedTask = ref<TimeModel | null>(null);
const draggedTask = ref<TimeModel | null>(null);
const draggedTaskOriginalTime = ref<Date | null>(null);
const dragOverKey = ref<string | null>(null);
const showDatePicker = ref(false);
const pickerValue = computed(() => {
    const d = currentDate.value;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
});

// Resize state
const resizingTask = ref<TimeModel | null>(null);
const resizeStartY = ref(0);
const resizeStartDuration = ref(0); // minutes
const resizeCurrentDuration = ref(0); // minutes - live preview
const HOUR_HEIGHT_PX = 60;

// Context menu state
const contextMenuRef = ref<InstanceType<typeof KtContextMenu> | null>(null);
const contextMenuTask = ref<TimeModel | null>(null);
const contextMenuItems = ref<ContextMenuItem[]>([]);
const contextMenuType = ref<'task' | 'day' | null>(null);
const contextMenuDate = ref<Date | null>(null);
const contextMenuHour = ref<number | null>(null);

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const timeTypeOptions = ref([
    { id: 'All', name: 'All' },
    { id: 'Assignments', name: 'Assignments' },
    { id: 'Work', name: 'Work' }
]);

const filters = ref({
    timeType: 'All',
    technician: [] as number[]
});

// ===== URL FILTER UTILITIES =====

const parseQueryToFilters = () => {
    const q = route.query;
    const techParam = q.technician;
    const technician: number[] = Array.isArray(techParam)
        ? techParam.filter(Boolean).map(t => parseInt(t as string))
        : techParam ? [parseInt(techParam as string)] : [];
    const timeType = (q.timeType as string) || 'All';
    const view = (q.view as string) as 'month' | 'week' | 'day' | undefined;
    return { timeType, technician, view };
};

const buildUrlFromFilters = (): string => {
    const params = new URLSearchParams();
    if (filters.value.timeType && filters.value.timeType !== 'All') {
        params.set('timeType', filters.value.timeType);
    }
    filters.value.technician.forEach(t => params.append('technician', t.toString()));
    params.set('view', viewMode.value);
    const qs = params.toString();
    return `/calendar${qs ? '?' + qs : ''}`;
};

const buildDefaultUrl = (): string => {
    const params = new URLSearchParams();
    params.set('view', 'month');
    const uid = userId.value;
    if (uid) {
        params.append('technician', uid.toString());
    }
    return `/calendar?${params.toString()}`;
};

const syncFiltersFromUrl = () => {
    const { timeType, technician, view } = parseQueryToFilters();
    filters.value.timeType = timeType;
    filters.value.technician = technician;
    if (view && ['month', 'week', 'day'].includes(view)) {
        viewMode.value = view as 'month' | 'week' | 'day';
    }
};

const applyFilter = async () => {
    const newUrl = buildUrlFromFilters();
    await Cache.set(CACHE_KEY, newUrl);
    router.replace(newUrl);
};

const applyDefaultView = () => {
    const uid = userId.value;
    if (uid) {
        filters.value.technician = [uid as number];
    }
    filters.value.timeType = 'Assignments';
    viewMode.value = 'month';
    loadTasks();
};

const clearFilters = async () => {
    await Cache.unset(CACHE_KEY);
    applyDefaultView();
};

const hasActiveFilters = computed(() => {
    return Object.keys(route.query).length > 0;
});

// Computed
const currentPeriodLabel = computed(() => {
    if (viewMode.value === 'month') {
        return dayjs(currentDate.value).format('MMMM YYYY');
    } else if (viewMode.value === 'week') {
        const start = weekDates.value[0];
        const end = weekDates.value[6];
        return `${dayjs(start).format('MMM D')} - ${dayjs(end).format('MMM D, YYYY')}`;
    } else {
        return dayjs(currentDate.value).format('dddd, MMMM D, YYYY');
    }
});

const monthDays = computed(() => {
    const year = currentDate.value.getFullYear();
    const month = currentDate.value.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const prevLastDay = new Date(year, month, 0);
    
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const daysInPrevMonth = prevLastDay.getDate();
    
    const days = [];
    
    // Previous month days
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const day = daysInPrevMonth - i;
        const date = new Date(year, month - 1, day);
        days.push({
            dayNumber: day,
            date: date,
            isCurrentMonth: false,
            isToday: false,
            isWeekend: date.getDay() === 0 || date.getDay() === 6
        });
    }
    
    // Current month days
    const today = new Date();
    for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(year, month, i);
        const isToday = date.getFullYear() === today.getFullYear() &&
                        date.getMonth() === today.getMonth() &&
                        date.getDate() === today.getDate();
        days.push({
            dayNumber: i,
            date: date,
            isCurrentMonth: true,
            isToday: isToday,
            isWeekend: date.getDay() === 0 || date.getDay() === 6
        });
    }
    
    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        days.push({
            dayNumber: i,
            date: date,
            isCurrentMonth: false,
            isToday: false,
            isWeekend: date.getDay() === 0 || date.getDay() === 6
        });
    }
    
    return days;
});

const weekDates = computed(() => {
    const dates = [];
    const year = currentDate.value.getFullYear();
    const month = currentDate.value.getMonth();
    const day = currentDate.value.getDate();
    const startOfWeek = new Date(year, month, day);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
    
    for (let i = 0; i < 7; i++) {
        const date = new Date(
            startOfWeek.getFullYear(),
            startOfWeek.getMonth(),
            startOfWeek.getDate() + i
        );
        dates.push(date);
    }
    
    return dates;
});

// Methods
const loadTasks = async () => {
    try {
        let startDate: Date;
        let endDate: Date;
        
        if (viewMode.value === 'month') {
            const year = currentDate.value.getFullYear();
            const month = currentDate.value.getMonth();
            startDate = new Date(year, month, 1);
            startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday before month
            endDate = new Date(year, month + 1, 0);
            endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // End on Saturday after month
        } else if (viewMode.value === 'week') {
            startDate = weekDates.value[0];
            endDate = weekDates.value[6];
        } else {
            startDate = new Date(currentDate.value);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(currentDate.value);
            endDate.setHours(23, 59, 59, 999);
        }
        
        const params: any = {
            StartDate: startDate,
            EndDate: endDate,
            IncludeRequest: true,
            IncludeContact: true,
            IncludeTechnician: true,
            Size: 1000
        };
        
        if (filters.value.timeType !== 'All') {
            params.TimeType = filters.value.timeType;
        }
        
        if (filters.value.technician.length > 0) {
            params.TechId = filters.value.technician;
        }
        
        const response = await Api.loadTimes(params, false);
        tasks.value = response.Data || [];
    } catch (error) {
        console.error('Failed to load tasks:', error);
        tasks.value = [];
    }
};

const getTasksForDay = (date: Date) => {
    return tasks.value.filter(task => {
        const taskDate = task.CompleteByDate || task.WorkCompleteDate;
        if (!taskDate) return false;
        const taskDay = Helper.dateFromString(taskDate);
        if (!taskDay) return false;
        return taskDay.getFullYear() === date.getFullYear() &&
               taskDay.getMonth() === date.getMonth() &&
               taskDay.getDate() === date.getDate();
    }).sort((a, b) => {
        const dateA = Helper.dateFromString(a.CompleteByDate || a.WorkCompleteDate || '');
        const dateB = Helper.dateFromString(b.CompleteByDate || b.WorkCompleteDate || '');
        return (dateA?.getTime() ?? 0) - (dateB?.getTime() ?? 0);
    });
};

const getTasksForDayHour = (date: Date, hour: number) => {
    return tasks.value.filter(task => {
        const taskDate = task.CompleteByDate || task.WorkCompleteDate;
        if (!taskDate) return false;
        const taskDay = Helper.dateFromString(taskDate);
        if (!taskDay) return false;
        return taskDay.getFullYear() === date.getFullYear() &&
               taskDay.getMonth() === date.getMonth() &&
               taskDay.getDate() === date.getDate() &&
               taskDay.getHours() === hour;
    }).sort((a, b) => {
        const dateA = Helper.dateFromString(a.CompleteByDate || a.WorkCompleteDate || '');
        const dateB = Helper.dateFromString(b.CompleteByDate || b.WorkCompleteDate || '');
        return (dateA?.getTime() ?? 0) - (dateB?.getTime() ?? 0);
    });
};

const getTaskTitle = (task: TimeModel) => {
    if (task.WorkAssignment) return task.WorkAssignment;
    if (task.ActualWork) return task.ActualWork;
    if (task.InvoiceText) return task.InvoiceText;
    return `Task #${task.Id}`;
};

const getTaskClass = (task: TimeModel) => {
    const classes = [];
    
    // Assignment vs Work
    if (task.WorkAssignment && !task.ActualWork) {
        classes.push('task-assignment');
    } else {
        classes.push('task-work');
    }
    
    // Status-based coloring
    if (task.Status) {
        classes.push(`task-status-${task.Status}`);
    }
    
    if (selectedTask.value?.Id === task.Id) {
        classes.push('task-selected');
    }
    
    return classes.join(' ');
};

const formatTime = (dateStr: string | Date | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return dayjs(date).format('h:mm A');
};

const formatDate = (date: Date) => {
    return dayjs(date).format('M/D');
};

const formatHour = (hour: number) => {
    return dayjs().hour(hour).minute(0).format('h A');
};

const formatDayHeader = (date: Date) => {
    return dayjs(date).format('dddd, MMMM D, YYYY');
};

const getTaskTypeLabel = (task: TimeModel) => {
    // Determine if it's Work or Task/Assignment
    return (task.WorkAssignment && !task.ActualWork) ? 'Task' : 'Work';
};

// ===== TASK TIME CALCULATIONS =====

interface TaskTimeBounds {
    start: Date;
    end: Date;
    durationMinutes: number;
}

const getTaskTimeBounds = (task: TimeModel): TaskTimeBounds | null => {
    const isAssignment = task.WorkAssignment && !task.ActualWork;
    const dateStr = task.CompleteByDate || task.WorkCompleteDate;
    
    if (!dateStr) return null;
    
    const endDate = new Date(dateStr);
    if (isNaN(endDate.getTime())) return null;
    
    let durationMinutes = 30; // Default 30 minutes
    
    if (isAssignment) {
        // Task: CompleteByDate is the START time ("Start By" in the form), EstimatedTime is duration in hours
        if (task.EstimatedTime && task.EstimatedTime > 0) {
            durationMinutes = Math.round(task.EstimatedTime * 60);
        }
        const start = endDate; // endDate variable holds the CompleteByDate (start time)
        const end = new Date(start.getTime() + durationMinutes * 60000);
        return { start, end, durationMinutes };
    } else {
        // Work: WorkCompleteDate is the END time, ActualTime is duration in hours
        if (task.ActualTime && task.ActualTime > 0) {
            durationMinutes = Math.round(task.ActualTime * 60);
        }
        const end = endDate;
        const start = new Date(end.getTime() - durationMinutes * 60000);
        return { start, end, durationMinutes };
    }
};

const getTaskStartMinutes = (task: TimeModel): number => {
    const bounds = getTaskTimeBounds(task);
    if (!bounds) return 0;
    return bounds.start.getHours() * 60 + bounds.start.getMinutes();
};

const getTaskDurationMinutes = (task: TimeModel): number => {
    const bounds = getTaskTimeBounds(task);
    return bounds?.durationMinutes || 30;
};

// ===== OVERLAP DETECTION =====

interface PositionedTask {
    task: TimeModel;
    startMinutes: number;
    endMinutes: number;
    column: number;
    totalColumns: number;
}

const doEventsOverlap = (a: PositionedTask, b: PositionedTask): boolean => {
    return a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;
};

const calculateEventLayout = (tasks: TimeModel[]): PositionedTask[] => {
    if (tasks.length === 0) return [];
    
    // First, calculate time bounds for all tasks
    const positioned: PositionedTask[] = tasks.map(task => {
        const bounds = getTaskTimeBounds(task);
        const startMinutes = bounds ? bounds.start.getHours() * 60 + bounds.start.getMinutes() : 0;
        const endMinutes = bounds ? startMinutes + bounds.durationMinutes : startMinutes + 30;
        return { task, startMinutes, endMinutes, column: 0, totalColumns: 1 };
    });
    
    // Assign columns using a simple greedy algorithm (max 2 columns)
    for (let i = 0; i < positioned.length; i++) {
        const current = positioned[i];
        
        // Find all overlapping events before this one
        const overlapping: PositionedTask[] = [];
        for (let j = 0; j < i; j++) {
            if (doEventsOverlap(current, positioned[j])) {
                overlapping.push(positioned[j]);
            }
        }
        
        if (overlapping.length === 0) {
            // No overlaps - takes full width
            current.column = 0;
            current.totalColumns = 1;
        } else {
            // Find which column is available (0 or 1)
            const usedColumns = new Set(overlapping.map(o => o.column));
            
            if (!usedColumns.has(0)) {
                current.column = 0;
                current.totalColumns = Math.min(2, overlapping.length + 1);
            } else if (!usedColumns.has(1)) {
                current.column = 1;
                current.totalColumns = 2;
            } else {
                // Both columns used - default to column 0 (will overlap visually)
                current.column = 0;
                current.totalColumns = 2;
            }
            
            // Update totalColumns for all overlapping events
            overlapping.forEach(o => {
                o.totalColumns = Math.max(o.totalColumns, current.totalColumns);
            });
        }
    }
    
    return positioned;
};

// ===== CONTEXT MENU =====

const getTaskTooltip = (task: TimeModel): string => {
    const lines: string[] = [];
    const dateStr = task.CompleteByDate || task.WorkCompleteDate;
    
    if (dateStr) {
        lines.push(`Time: ${formatTime(dateStr)}`);
    }
    lines.push(`Type: ${getTaskTypeLabel(task)}`);
    lines.push(`Title: ${getTaskTitle(task)}`);
    
    const bounds = getTaskTimeBounds(task);
    if (bounds) {
        const hours = Math.floor(bounds.durationMinutes / 60);
        const mins = bounds.durationMinutes % 60;
        if (hours > 0) {
            lines.push(`Duration: ${hours}h ${mins > 0 ? mins + 'm' : ''}`);
        } else {
            lines.push(`Duration: ${mins}m`);
        }
    }
    
    if (task.Technician) {
        lines.push(`Tech: ${task.Technician.TechnicianName}`);
    }
    const requestWithContact = task.Request as any;
    if (requestWithContact?.Contact?.Company) {
        lines.push(`Client: ${requestWithContact.Contact.Company}`);
    }
    
    return lines.join('\n');
};

const handleTaskContextMenu = (event: MouseEvent, task: TimeModel) => {
    event.preventDefault();
    event.stopPropagation();
    
    contextMenuType.value = 'task';
    contextMenuTask.value = task;
    contextMenuDate.value = null;
    contextMenuHour.value = null;
    
    const items: ContextMenuItem[] = [];
    const dateStr = task.CompleteByDate || task.WorkCompleteDate;
    
    // Header info item (disabled, for display only)
    items.push({
        id: 'header',
        label: `${getTaskTypeLabel(task)}: ${getTaskTitle(task)}`,
        disabled: true
    });
    
    items.push({ id: 'separator1', label: '', disabled: true });
    
    if (dateStr) {
        items.push({
            id: 'time',
            label: `Time: ${formatTime(dateStr)}`,
            icon: 'fas fa-clock',
            disabled: true
        });
    }
    
    const bounds = getTaskTimeBounds(task);
    if (bounds) {
        const hours = Math.floor(bounds.durationMinutes / 60);
        const mins = bounds.durationMinutes % 60;
        const durationText = hours > 0 
            ? `${hours}h ${mins > 0 ? mins + 'm' : ''}` 
            : `${mins}m`;
        items.push({
            id: 'duration',
            label: `Duration: ${durationText}`,
            icon: 'fas fa-hourglass-half',
            disabled: true
        });
    }
    
    if (task.Technician) {
        items.push({
            id: 'tech',
            label: `Tech: ${task.Technician.TechnicianName}`,
            icon: 'fas fa-user',
            disabled: true
        });
    }
    
    const requestWithContact = task.Request as any;
    if (requestWithContact?.Contact?.Company) {
        items.push({
            id: 'client',
            label: `Client: ${requestWithContact.Contact.Company}`,
            icon: 'fas fa-building',
            disabled: true
        });
    }
    
    items.push({ id: 'separator2', label: '', disabled: true });
    
    items.push({
        id: 'edit',
        label: 'Edit Task',
        icon: 'fas fa-edit'
    });
    
    contextMenuItems.value = items;
    contextMenuRef.value?.show(event.clientX, event.clientY);
};

const handleDayContextMenu = (event: MouseEvent, date: Date, hour?: number) => {
    event.preventDefault();
    event.stopPropagation();
    
    contextMenuType.value = 'day';
    contextMenuTask.value = null;
    contextMenuDate.value = date;
    contextMenuHour.value = hour ?? null;
    
    const items: ContextMenuItem[] = [];
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dateStr = `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    if (hour !== undefined) {
        const timeStr = dayjs().hour(hour).minute(0).format('h:mm A');
        items.push({
            id: 'header',
            label: `${dateStr} at ${timeStr}`,
            disabled: true
        });
    } else {
        items.push({
            id: 'header',
            label: dateStr,
            disabled: true
        });
    }
    
    items.push({ id: 'separator1', label: '', disabled: true });
    
    items.push({
        id: 'newTask',
        label: 'New Task',
        icon: 'fas fa-plus'
    });
    
    items.push({
        id: 'newWork',
        label: 'New Work Entry',
        icon: 'fas fa-briefcase'
    });
    
    contextMenuItems.value = items;
    contextMenuRef.value?.show(event.clientX, event.clientY);
};

const handleContextMenuSelect = (item: ContextMenuItem) => {
    // Handle task context menu
    if (contextMenuType.value === 'task' && contextMenuTask.value) {
        switch (item.id) {
            case 'edit':
                openTaskModal(contextMenuTask.value);
                break;
        }
        contextMenuTask.value = null;
    }
    
    // Handle day/time slot context menu
    if (contextMenuType.value === 'day' && contextMenuDate.value) {
        // Calculate the date/time for the new task
        let newDate: Date;
        
        if (contextMenuHour.value !== null) {
            const d = contextMenuDate.value;
            newDate = new Date(
                d.getFullYear(),
                d.getMonth(),
                d.getDate(),
                contextMenuHour.value,
                0, 0, 0
            );
        } else {
            // Month view - default to noon local time
            const d = contextMenuDate.value;
            newDate = new Date(
                d.getFullYear(),
                d.getMonth(),
                d.getDate(),
                12, 0, 0, 0
            );
        }
        
        const dateTimeString = dayjs(newDate).format('YYYY-MM-DDTHH:mm');
        
        switch (item.id) {
            case 'newTask':
                Emitter.emit('open:taskForm', {
                    requestId: null,
                    clientId: null,
                    techId: userId.value || null,
                    completeByDate: dateTimeString
                });
                break;
            case 'newWork':
                Emitter.emit('open:work', {
                    id: null,
                    requestId: null,
                    clientId: null,
                    techId: userId.value || null,
                    workCompleteDate: dateTimeString
                });
                break;
        }
        
        contextMenuDate.value = null;
        contextMenuHour.value = null;
    }
    
    contextMenuType.value = null;
};

// Listen for global modal close events to refresh data
let unsubAssignmentSaved: Function;
let unsubWorkSaved: Function;

const closeDatePicker = () => { showDatePicker.value = false; };

onMounted(() => {
    unsubAssignmentSaved = Emitter.on('assignment:saved', handleModalSaved);
    unsubWorkSaved = Emitter.on('work:saved', handleModalSaved);
    document.addEventListener('click', closeDatePicker);
});

onUnmounted(() => {
    unsubAssignmentSaved?.();
    unsubWorkSaved?.();
    document.removeEventListener('click', closeDatePicker);
});

// ===== POSITIONED TASK HELPERS FOR DAY/WEEK VIEWS =====

const getPositionedTasksForDay = (date: Date): PositionedTask[] => {
    const dayTasks = tasks.value.filter(task => {
        const taskDate = task.CompleteByDate || task.WorkCompleteDate;
        if (!taskDate) return false;
        const taskDay = Helper.dateFromString(taskDate);
        if (!taskDay) return false;
        return taskDay.getFullYear() === date.getFullYear() &&
               taskDay.getMonth() === date.getMonth() &&
               taskDay.getDate() === date.getDate();
    });
    
    return calculateEventLayout(dayTasks);
};

const getOverlapClass = (positioned: PositionedTask): string => {
    if (positioned.totalColumns <= 1) return '';
    return `task-overlap-${positioned.totalColumns} task-col-${positioned.column}`;
};

const getTaskPositionStyle = (positioned: PositionedTask): Record<string, string> => {
    const HOUR_HEIGHT = 60; // pixels per hour (from CSS)
    const top = (positioned.startMinutes / 60) * HOUR_HEIGHT;
    // Use live resize duration if this task is currently being resized
    const durationMinutes = (resizingTask.value?.Id && resizingTask.value.Id === positioned.task.Id)
        ? resizeCurrentDuration.value
        : (positioned.endMinutes - positioned.startMinutes);
    const height = (durationMinutes / 60) * HOUR_HEIGHT;
    
    const style: Record<string, string> = {
        top: `${top}px`,
        height: `${Math.max(height, 30)}px` // Minimum 30px height for visibility
    };
    
    // Width and left position based on overlap
    if (positioned.totalColumns > 1) {
        const widthPercent = 100 / positioned.totalColumns;
        style.width = `${widthPercent}%`;
        style.left = `${positioned.column * widthPercent}%`;
    }
    
    return style;
};

const formatTaskTimeRange = (task: TimeModel): string => {
    const bounds = getTaskTimeBounds(task);
    if (!bounds) return '';
    
    const startTime = dayjs(bounds.start).format('h:mm A');
    const endTime = dayjs(bounds.end).format('h:mm A');
    return `${startTime} - ${endTime}`;
};

const isToday = (date: Date) => {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
};

const previousPeriod = () => {
    if (viewMode.value === 'month') {
        const year = currentDate.value.getFullYear();
        const month = currentDate.value.getMonth();
        currentDate.value = new Date(year, month - 1, 1);
    } else if (viewMode.value === 'week') {
        const year = currentDate.value.getFullYear();
        const month = currentDate.value.getMonth();
        const day = currentDate.value.getDate();
        currentDate.value = new Date(year, month, day - 7);
    } else {
        const year = currentDate.value.getFullYear();
        const month = currentDate.value.getMonth();
        const day = currentDate.value.getDate();
        currentDate.value = new Date(year, month, day - 1);
    }
    loadTasks();
};

const nextPeriod = () => {
    if (viewMode.value === 'month') {
        const year = currentDate.value.getFullYear();
        const month = currentDate.value.getMonth();
        currentDate.value = new Date(year, month + 1, 1);
    } else if (viewMode.value === 'week') {
        const year = currentDate.value.getFullYear();
        const month = currentDate.value.getMonth();
        const day = currentDate.value.getDate();
        currentDate.value = new Date(year, month, day + 7);
    } else {
        const year = currentDate.value.getFullYear();
        const month = currentDate.value.getMonth();
        const day = currentDate.value.getDate();
        currentDate.value = new Date(year, month, day + 1);
    }
    loadTasks();
};

const goToToday = () => {
    currentDate.value = new Date();
    loadTasks();
};

const goToDate = (dateStr: string) => {
    if (!dateStr) return;
    currentDate.value = new Date(dateStr + 'T12:00:00');
    loadTasks();
};

const selectTask = (task: TimeModel) => {
    // Toggle: deselect if clicking the same task
    if (selectedTask.value?.Id === task.Id) {
        selectedTask.value = null;
    } else {
        selectedTask.value = task;
    }
};

const deselectTask = () => {
    selectedTask.value = null;
};

const openTaskModal = (task: TimeModel | null) => {
    const taskToEdit = task || selectedTask.value;
    if (taskToEdit && taskToEdit.Id) {
        // Determine task type: Assignment has WorkAssignment but no ActualWork
        const isAssignment = taskToEdit.WorkAssignment && !taskToEdit.ActualWork;
        
        if (isAssignment) {
            // Open assignment/task form
            Emitter.emit('open:taskForm', {
                id: taskToEdit.Id,
                requestId: taskToEdit.RequestId || null,
                clientId: taskToEdit.CustomerNumber || null,
                techId: taskToEdit.AssignedTo || null
            });
        } else {
            // Open work entry form
            Emitter.emit('open:work', {
                id: taskToEdit.Id,
                requestId: taskToEdit.RequestId || null,
                clientId: taskToEdit.CustomerNumber || null,
                techId: taskToEdit.AssignedTo || null
            });
        }
    } else {
        // New task - open assignment form by default
        Emitter.emit('open:taskForm', {
            requestId: null,
            clientId: null,
            techId: null
        });
    }
};

// Listen for modal close events to refresh calendar
const handleModalSaved = () => {
    loadTasks();
};

const handleDragStart = (event: DragEvent, task: TimeModel) => {
    draggedTask.value = task;
    const dateStr = task.CompleteByDate || task.WorkCompleteDate;
    draggedTaskOriginalTime.value = dateStr ? Helper.dateFromString(dateStr) : null;
    event.dataTransfer!.effectAllowed = 'move';
};

// ===== DRAG OVER HANDLING FOR 30-MIN SNAP =====

const handleTimeSlotDragOver = (event: DragEvent, date: Date, hour: number) => {
    event.preventDefault();
    // Could add visual feedback here in the future
};

const handleDrop = async (event: DragEvent, date: Date) => {
    event.preventDefault();
    if (!draggedTask.value) return;
    
    const task = draggedTask.value;
    const original = draggedTaskOriginalTime.value;
    draggedTask.value = null;
    draggedTaskOriginalTime.value = null;
    
    try {
        const isAssignment = !!(task.WorkAssignment && !task.ActualWork);
        
        // Preserve original time-of-day, just change the calendar date
        const hours = original ? original.getHours() : 12;
        const minutes = original ? original.getMinutes() : 0;
        
        const newDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            hours,
            minutes,
            0, 0
        );
        
        const dateStr = dayjs(newDate).format('YYYY-MM-DDTHH:mm');

        if (isAssignment) {
            task.CompleteByDate = dateStr;
        } else {
            task.WorkCompleteDate = dateStr;
        }
        
        await Api.saveTime(task, true);
        await loadTasks();
    } catch (error) {
        console.error('Failed to save dropped task:', error);
        await loadTasks();
    }
};

const handleDropTime = async (event: DragEvent, date: Date, hour: number) => {
    event.preventDefault();
    if (!draggedTask.value) return;
    
    const task = draggedTask.value;
    draggedTask.value = null;
    draggedTaskOriginalTime.value = null;
    
    try {
        const isAssignment = !!(task.WorkAssignment && !task.ActualWork);
        
        // 30-minute snap from mouse Y position within the hour slot
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const relativeY = event.clientY - rect.top;
        const snapMinutes = relativeY / rect.height >= 0.5 ? 30 : 0;
        
        const newDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            hour,
            snapMinutes,
            0, 0
        );
        
        const dateStr = dayjs(newDate).format('YYYY-MM-DDTHH:mm');
        
        if (isAssignment) {
            task.CompleteByDate = dateStr;
        } else {
            task.WorkCompleteDate = dateStr;
        }
        
        await Api.saveTime(task, true);
        await loadTasks();
    } catch (error) {
        console.error('Failed to save dropped task:', error);
        await loadTasks();
    }
};

// ===== RESIZE HANDLERS =====

const startResize = (event: MouseEvent, task: TimeModel, _view: 'week' | 'day') => {
    resizingTask.value = task;
    resizeStartY.value = event.clientY;
    const bounds = getTaskTimeBounds(task);
    resizeStartDuration.value = bounds?.durationMinutes ?? 30;
    resizeCurrentDuration.value = resizeStartDuration.value;

    const onMouseMove = (e: MouseEvent) => {
        const deltaY = e.clientY - resizeStartY.value;
        const deltaMinutes = (deltaY / HOUR_HEIGHT_PX) * 60;
        // Snap to 15-minute increments, minimum 15 minutes
        const raw = resizeStartDuration.value + deltaMinutes;
        resizeCurrentDuration.value = Math.max(15, Math.round(raw / 15) * 15);
    };

    const onMouseUp = async () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);

        if (!resizingTask.value) return;
        const t = resizingTask.value;
        const finalDuration = resizeCurrentDuration.value;
        resizingTask.value = null;

        // Apply the final duration to the task model before saving
        const isAssignment = !!(t.WorkAssignment && !t.ActualWork);
        if (isAssignment) {
            t.EstimatedTime = finalDuration / 60;
        } else {
            t.ActualTime = finalDuration / 60;
        }

        try {
            await Api.saveTime(t, true);
            await loadTasks();
        } catch (error) {
            console.error('Failed to save resized task:', error);
            await loadTasks();
        }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
};

// Watchers

// When viewMode changes via the view buttons, update URL (applyFilter triggers route.query watcher -> loadTasks)
watch(viewMode, () => {
    applyFilter();
});

// When route.query changes (after router.replace), sync filters and reload
watch(() => route.query, (newQuery, oldQuery) => {
    if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
        syncFiltersFromUrl();
        loadTasks();
    }
}, { deep: true });

// Lifecycle
const initCalendar = async () => {
    const isReset = route.path.endsWith('/reset');

    if (isReset) {
        await Cache.unset(CACHE_KEY);
        router.replace('/calendar');
        return;
    }

    const hasQueryParams = Object.keys(route.query).length > 0;

    if (hasQueryParams) {
        // URL has filter params — use as source of truth, save to cache
        syncFiltersFromUrl();
        await Cache.set(CACHE_KEY, route.fullPath.replace(/^.*#/, ''));
        loadTasks();
    } else {
        // Bare /calendar — check cache for saved filter URL, redirect if found
        const savedUrl = await Cache.get(CACHE_KEY);
        if (savedUrl) {
            router.replace(savedUrl);
            // route.query watcher will sync + loadTasks
        } else {
            // No cache — apply default view in-place
            applyDefaultView();
        }
    }
};

onMounted(initCalendar);
</script>

<style scoped>
.calendar-page {
    padding: 1rem;
    height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
}

.calendar-toolbar {
    flex-shrink: 0;
}

.calendar-container {
    flex: 1;
    overflow: hidden;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Month View */
.calendar-month {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
}

.calendar-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 10;
}

.calendar-day-header {
    padding: 0.75rem;
    text-align: center;
    font-weight: 600;
    border-right: 1px solid #dee2e6;
}

.calendar-day-header:last-child {
    border-right: none;
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-auto-rows: minmax(100px, auto);
    flex: 1;
}

.calendar-day {
    border: 1px solid #dee2e6;
    padding: 0.5rem;
    min-height: 100px;
    background: white;
    cursor: pointer;
    overflow: auto;
}

.calendar-day:hover {
    background: #f8f9fa;
}

.calendar-day.drag-over {
    background: #cfe2ff;
    border: 1px dashed #0d6efd;
}

.calendar-day.other-month {
    background: #f1f3f5;
    color: #adb5bd;
}

.calendar-day.today {
    background: #e7f5ff;
    border-color: #0d6efd;
}

.calendar-day.weekend {
    background: #f8f9fa;
}

.day-number {
    font-weight: 600;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
}

.day-tasks {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.task-item {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: move;
    border-left: 3px solid;
}

.task-item:hover {
    opacity: 0.8;
}

.task-assignment {
    background: #ffe8cc;
    border-left-color: #e8720c;
}

.task-work {
    background: #c8edcc;
    border-left-color: #1a7a35;
}

.task-selected {
    box-shadow: 0 0 0 2px #0d6efd;
}

.task-time {
    font-weight: 600;
    color: #495057;
}

.task-type-label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #0d6efd;
    margin-bottom: 0.125rem;
}

.task-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.task-tech,
.task-client {
    font-size: 0.7rem;
    color: #6c757d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 0.125rem;
}

.task-tech i,
.task-client i {
    font-size: 0.65rem;
    opacity: 0.7;
}

/* Week View */
.calendar-week {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.week-header {
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
    grid-column: 1 / -1;
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 10;
}

.time-column-header {
    padding: 0.75rem;
    text-align: center;
    font-weight: 600;
    border-right: 1px solid #dee2e6;
}

.week-day-header {
    padding: 0.75rem;
    text-align: center;
    border-right: 1px solid #dee2e6;
}

.week-day-header.today {
    background: #e7f5ff;
    font-weight: 600;
}

.day-name {
    font-weight: 600;
}

.day-date {
    font-size: 0.875rem;
    color: #6c757d;
}

.week-grid {
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
    grid-auto-rows: auto;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
}

/* Custom scrollbar styling to minimize width impact */
.week-grid::-webkit-scrollbar {
    width: 8px;
}

.week-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.week-grid::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.week-grid::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.time-column {
    grid-column: 1;
    border-right: 2px solid #dee2e6;
    background: #f8f9fa;
    display: flex;
    flex-direction: column;
}

.time-slot {
    padding: 0.5rem;
    height: 60px;
    border-bottom: 1px solid #dee2e6;
    font-size: 0.75rem;
    text-align: right;
}

.week-day-column {
    border-right: 1px solid #dee2e6;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.week-day-column.today {
    background: #f8f9ff;
}

.week-time-slot {
    height: 60px;
    border-bottom: 1px solid #e9ecef;
    position: relative;
    padding: 0.25rem;
    min-width: 0;
    overflow: hidden;
}

.week-time-slot:hover {
    background: #f8f9fa;
}

.week-time-slot.drag-over {
    background: #cfe2ff;
    border: 1px dashed #0d6efd;
}

.task-item-week {
    padding: 0.25rem;
    border-radius: 4px;
    font-size: 0.7rem;
    cursor: move;
    border-left: 3px solid;
    margin-bottom: 0.125rem;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

.task-title-week {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.task-job-label-week {
    font-size: 0.65rem;
    font-weight: 600;
    color: #343a40;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    opacity: 0.85;
}

.task-tech-week,
.task-client-week {
    font-size: 0.65rem;
    color: #6c757d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 0.125rem;
    min-width: 0;
}

.task-tech-week i,
.task-client-week i {
    font-size: 0.6rem;
    opacity: 0.7;
}

.task-type-label-week {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #0d6efd;
    margin-bottom: 0.125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

/* Day View */
.calendar-day-view {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.day-view-header {
    padding: 1rem;
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    text-align: center;
}

.day-view-grid {
    display: grid;
    grid-template-columns: 80px 1fr;
    flex: 1;
    overflow: auto;
}

.day-view-column {
    border-left: 2px solid #dee2e6;
}

.day-time-slot {
    height: 60px;
    border-bottom: 1px solid #e9ecef;
    position: relative;
    padding: 0.5rem;
}

.day-time-slot:hover {
    background: #f8f9fa;
}

.day-time-slot.drag-over {
    background: #cfe2ff;
    border: 1px dashed #0d6efd;
}

.task-item-day {
    padding: 0.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: move;
    border-left: 4px solid;
    margin-bottom: 0.25rem;
}

.task-time-day {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.25rem;
}

.task-title-day {
    margin-bottom: 0.25rem;
}

.task-tech {
    font-size: 0.75rem;
    color: #6c757d;
}

/* ===== COMPACT MONTHLY VIEW STYLES ===== */
.date-picker-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 1000;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    min-width: 160px;
}

.task-compact-job {
    font-weight: 600;
    color: #343a40;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    min-width: 0;
    opacity: 0.85;
}

.task-compact-line {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
}

.task-compact-time {
    font-weight: 600;
    color: #495057;
    flex-shrink: 0;
}

.task-compact-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Override task-item for monthly compact view */
.task-item {
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 0.75rem;
    cursor: move;
    border-left: 3px solid;
    height: 22px;
    min-height: 22px;
    max-height: 22px;
    overflow: hidden;
    display: flex;
    align-items: center;
}

/* ===== WEEK/DAY VIEW POSITIONED EVENTS ===== */
.week-day-column,
.day-view-column {
    position: relative;
}

/* Positioned task items for week view */
.task-item-week-positioned {
    position: absolute;
    left: 0;
    right: 4px;
    padding: 2px 4px;
    border-radius: 4px;
    font-size: 0.7rem;
    cursor: move;
    border-left: 3px solid;
    overflow: hidden;
    z-index: 5;
    box-sizing: border-box;
}

/* Positioned task items for day view */
.task-item-day-positioned {
    position: absolute;
    left: 0;
    right: 8px;
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: move;
    border-left: 4px solid;
    overflow: hidden;
    z-index: 5;
    box-sizing: border-box;
}

/* Hover effects for positioned events */
.task-item-week-positioned:hover,
.task-item-day-positioned:hover {
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

/* Resize handle at the bottom of week/day events */
.resize-handle {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 8px;
    cursor: ns-resize;
    background: linear-gradient(transparent, rgba(0,0,0,0.15));
    border-radius: 0 0 4px 4px;
}

.resize-handle::after {
    content: '';
    display: block;
    width: 24px;
    height: 3px;
    margin: 2px auto 0;
    border-top: 1px solid rgba(0,0,0,0.3);
    border-bottom: 1px solid rgba(0,0,0,0.3);
}

.resize-handle:hover {
    background: linear-gradient(transparent, rgba(0,0,0,0.25));
}

/* ===== OVERLAP HANDLING (MAX 2 COLUMNS) ===== */
.task-overlap-2 {
    width: 50% !important;
}

.task-col-0 {
    left: 0 !important;
}

.task-col-1 {
    left: 50% !important;
}

/* When 3+ events overlap, they stack (limited to 2 columns) */
.task-overlap-2.task-col-0 ~ .task-overlap-2.task-col-0,
.task-overlap-2.task-col-1 ~ .task-overlap-2.task-col-1 {
    z-index: 4;
}

/* ===== 30-MINUTE GRID LINES ===== */
.week-time-half,
.day-time-half {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(0,0,0,0.05);
    pointer-events: none;
}

.week-time-slot,
.day-time-slot {
    position: relative;
}

/* Week view time display */
.task-time-week {
    font-size: 0.6rem;
    color: #6c757d;
    margin-top: 1px;
}

.task-job-label-day {
    font-size: 0.75rem;
    font-weight: 600;
    color: #343a40;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 0.125rem;
    opacity: 0.85;
}

/* Day view type label */
.task-type-label-day {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #0d6efd;
    margin-bottom: 2px;
}

/* Selected task in positioned views */
.task-selected {
    box-shadow: 0 0 0 2px #0d6efd;
    z-index: 15 !important;
}
</style>
